import { createContext, useContext, useEffect, useRef } from "react";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && console.log(...args);

// Create a context to hold the registry
const TransactionHookRegistryContext = createContext({
  registerHook: () => {},
  unregisterHook: () => {},
  getRegisteredHooks: () => [],
});

// Create a provider component that manages the registry
export const TransactionHookRegistryProvider = ({ children }) => {
  // Use a ref to store the registry to ensure it persists between renders
  const registryRef = useRef(new Set());

  // Function to register a hook
  const registerHook = (hook) => {
    debugLog(
      "[TransactionRegistry] Registering hook with methods:",
      Object.keys(hook).filter((key) => typeof hook[key] === "function"),
    );
    registryRef.current.add(hook);
    return () => unregisterHook(hook);
  };

  // Function to unregister a hook
  const unregisterHook = (hook) => {
    debugLog("[TransactionRegistry] Unregistering hook");
    registryRef.current.delete(hook);
  };

  // Function to get all registered hooks
  const getRegisteredHooks = () => {
    return Array.from(registryRef.current);
  };

  // Create the context value
  const contextValue = {
    registerHook,
    unregisterHook,
    getRegisteredHooks,
  };

  return (
    <TransactionHookRegistryContext.Provider value={contextValue}>
      {children}
    </TransactionHookRegistryContext.Provider>
  );
};

// Hook for components to access the registry
export const useAccountTransactionsRegistry = () => {
  return useContext(TransactionHookRegistryContext);
};

// Hook for transaction hooks to register themselves with the registry
export const useRegisterAccountTransactionsHook = (hook) => {
  const { registerHook } = useAccountTransactionsRegistry();

  useEffect(() => {
    // Register the hook when the component mounts
    const unregister = registerHook(hook);

    // Unregister the hook when the component unmounts
    return unregister;
  }, [hook, registerHook]);
};
